/**
 * <JobInfoPanel>
 *
 * Reusable "Job Information" summary block styled to match the branded
 * Field Report documents (PreConDocument.js / SoftDigDocument.js /
 * LocateDocument.js — see the "Job Information" gray box there). Built
 * as a standalone component (not inline markup) so it can be reused
 * later for a PDF/archival export of Check-Out/Check-In/Maintenance
 * forms without rebuilding the layout.
 *
 * Consumes the same job shape returned by GET /api/jobs/for-field-reports
 * (job_number, job_title, service_address, date, assigned_team_names) —
 * the same object every <JobSelectionCard> caller already resolves.
 */
import React from 'react';
import { MapPin, Users } from 'lucide-react';

// Date-only strings ("2026-07-23") must NOT go through `new Date(str)` +
// a timeZone-aware Intl format — that parses as UTC midnight and can
// roll back a day once converted to Eastern Time. Same safe local-parse
// pattern already used by PreConDocument.js/SoftDigDocument.js.
function formatJobDate(dateStr) {
  if (!dateStr) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr).trim());
  const date = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function JobInfoPanel({ job, testIdPrefix = 'job-info' }) {
  if (!job) return null;
  return (
    <div className="p-3 bg-slate-50 rounded-lg" data-testid={`${testIdPrefix}-panel`}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
        Job Information
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <div>
          <p className="text-slate-500 text-[10px]">Job Number</p>
          <p className="font-medium text-amber-600" data-testid={`${testIdPrefix}-job-number`}>
            {job.job_number || '—'}
          </p>
        </div>
        <div>
          <p className="text-slate-500 text-[10px]">Description</p>
          <p className="font-medium text-slate-700" data-testid={`${testIdPrefix}-description`}>
            {job.job_title || '—'}
          </p>
        </div>
        <div>
          <p className="text-slate-500 text-[10px]">Date</p>
          <p className="font-medium text-slate-700" data-testid={`${testIdPrefix}-date`}>
            {formatJobDate(job.date)}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <div>
          <p className="text-slate-500 text-[10px]">Site Location</p>
          <p className="font-medium text-slate-700 flex items-center gap-1" data-testid={`${testIdPrefix}-address`}>
            <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
            {job.service_address || '—'}
          </p>
        </div>
        <div>
          <p className="text-slate-500 text-[10px]">Crew Members</p>
          <p className="font-medium text-slate-700 flex items-center gap-1" data-testid={`${testIdPrefix}-crew`}>
            <Users className="w-3 h-3 text-slate-400 flex-shrink-0" />
            {job.assigned_team_names?.length ? job.assigned_team_names.join(', ') : 'Not assigned'}
          </p>
        </div>
      </div>
    </div>
  );
}
